"use client";

import { useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { DEFAULT_ACCENT_ID, SPEAKER_PALETTE } from "@speakai/shared";
import {
  getThemeById,
  APP_FONTS,
  type AppFontId,
} from "@/config/themes";
import { isUserThemedRoute } from "@/config/constants";

const STORAGE_KEY_THEME = "app-theme";
const STORAGE_KEY_FONT = "app-font";
const FONT_LINK_ID = "app-custom-font";

/** CSS variables that user themes override. */
const THEME_CSS_VARS = [
  "--color-primary",
  "--color-primary-foreground",
  "--color-ring",
  "--color-gradient-from",
  "--color-gradient-to",
  "--color-gradient-accent",
] as const;

/**
 * Applies the user's saved theme + font on page load.
 * Route-aware: only applies on dashboard/onboarding routes.
 * Auth pages always use the default B&W theme defined in globals.css.
 *
 * Placed in root layout — runs on every page / navigation.
 * Does NOT render any UI.
 */
export function ThemeApplier() {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const { style } = document.documentElement;
    style.setProperty("--speaker-lightness-light", String(SPEAKER_PALETTE.lightness.light));
    style.setProperty("--speaker-lightness-dark", String(SPEAKER_PALETTE.lightness.dark));
    style.setProperty("--speaker-chroma", String(SPEAKER_PALETTE.chroma));
  }, []);

  useEffect(() => {
    const html = document.documentElement;

    if (!isUserThemedRoute(pathname)) {
      // Non-themed route: strip any inline theme overrides so the
      // globals.css monochrome defaults take effect.
      for (const prop of THEME_CSS_VARS) {
        html.style.removeProperty(prop);
      }
      html.style.removeProperty("--font-sans");

      // Remove dynamically-loaded Google Font link if present
      document.getElementById(FONT_LINK_ID)?.remove();
      return;
    }

    // Themed route: apply user-saved theme + font from localStorage.

    // Apply theme
    const savedTheme = localStorage.getItem(STORAGE_KEY_THEME);
    if (savedTheme && savedTheme !== DEFAULT_ACCENT_ID) {
      const theme = getThemeById(savedTheme);
      const isDark = html.classList.contains("dark");
      const tokens = isDark ? theme.tokens.dark : theme.tokens.light;
      for (const [key, value] of Object.entries(tokens)) {
        html.style.setProperty(key, value);
      }
    } else {
      // Default theme selected — clear any stale inline overrides
      for (const prop of THEME_CSS_VARS) {
        html.style.removeProperty(prop);
      }
    }

    // Apply font
    const savedFont = localStorage.getItem(STORAGE_KEY_FONT) as
      | AppFontId
      | null;
    if (savedFont && savedFont !== "dm-sans") {
      const font = APP_FONTS.find((f) => f.id === savedFont);
      if (font?.family) {
        // Load Google Font if not already present
        if (!document.getElementById(FONT_LINK_ID)) {
          const link = document.createElement("link");
          link.id = FONT_LINK_ID;
          link.rel = "stylesheet";
          link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(font.family)}:wght@300;400;500;600;700&display=swap`;
          document.head.appendChild(link);
        }

        html.style.setProperty(
          "--font-sans",
          `"${font.family}", ui-sans-serif, system-ui, sans-serif`,
        );
      } else if (savedFont === "system") {
        html.style.removeProperty("--font-sans");
      }
    } else {
      // Default font — clear any stale override
      html.style.removeProperty("--font-sans");
      document.getElementById(FONT_LINK_ID)?.remove();
    }
  }, [pathname]);

  return null;
}
